import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  ArrowRight,
  BadgeDollarSign,
  BarChart3,
  CheckCircle2,
  Cloud,
  Coins,
  CreditCard,
  Cpu,
  Gauge,
  History,
  Lock,
  RefreshCcw,
  Save,
  Server,
  ShieldCheck,
  SlidersHorizontal,
  ShoppingCart,
  Wallet,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";

import { CreditAmount } from "@/components/genrl/CreditAmount";
import { APIError, apiRequest } from "@/lib/api";
import { MICRO, formatMicros } from "@/lib/genrl";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/store")({
  head: () => ({
    meta: [
      { title: "Store · GENRL Wallet" },
      { name: "description", content: "Buy provider-specific compute credits." },
    ],
  }),
  validateSearch: (raw: Record<string, unknown>) => ({
    stripe_status: typeof raw.stripe_status === "string" ? raw.stripe_status : undefined,
    checkout_id: typeof raw.checkout_id === "string" ? raw.checkout_id : undefined,
    session_id: typeof raw.session_id === "string" ? raw.session_id : undefined,
    provider_order_id: typeof raw.provider_order_id === "string" ? raw.provider_order_id : undefined,
  }),
  component: StorePage,
});

type ProviderSlug = "runpod" | "hyperstack" | "lambda";
type FundingMethod = "genrl_credits" | "stripe";
type ProVisibility = "private" | "network";
type ProBroadcastMode = "INTERNAL_ONLY" | "BOTH" | "BROADCAST_ONLY";
type ProPricingMode = "PASSTHROUGH" | "PREMIUM_OR_DISCOUNT" | "FIXED" | "FLOOR_ADJUSTED";

type StoredAuthUserRef = {
  email: string;
};

type BackendCreditBalance = {
  available_credit_micros?: number;
  reserved_credit_micros?: number;
  spent_credit_micros?: number;
  total_credit_micros?: number;
  earned_credit_micros?: number;
  pending_payout_credit_micros?: number;
};

type BackendStripeConfig = {
  ready?: boolean;
  checkout_configured?: boolean;
  webhook_configured?: boolean;
};

type BackendProviderCreditOrder = {
  id: string;
  provider_slug: string;
  provider_name: string;
  provider_credit_amount_micros: number;
  payment_amount_micros: number;
  funding_method: string;
  status: string;
  fulfillment_status: string;
  stripe_checkout_id?: string;
  credited_transaction_id?: string;
  last_error?: string;
  created_at: string;
  updated_at: string;
};

type BackendProviderCreditAsset = {
  provider_id: string;
  total_balance_micros: number;
  available_balance_micros: number;
  escrowed_balance_micros: number;
  marketplace_escrow_balance_micros?: number;
  spent_balance_micros: number;
};

type BackendProProvider = {
  id: string;
  wallet_account_id?: string;
  provider_slug: string;
  provider_name: string;
  display_name: string;
  credential_last4?: string;
  has_credential?: boolean;
  status: string;
  visibility: string;
  broadcast_mode: string;
  pricing_mode: string;
  provider_adjustment_bps?: number;
  fixed_unit_price_micros?: number;
  min_unit_price_micros?: number;
  max_unit_price_micros?: number;
  reserve_balance_threshold_micros?: number;
  broadcast_stock_limit?: number;
  allowed_regions?: string[];
  allowed_hardware?: string[];
  capabilities?: Record<string, unknown>;
  health?: Record<string, unknown>;
  earned_credit_micros?: number;
  owner_pending_payout_micros?: number;
  settled_jobs?: number;
  last_settlement_at?: string;
  last_validated_at?: string;
  last_error?: string;
  updated_at?: string;
};

type BackendMarketOffer = {
  source?: string;
  provider_slug?: string;
  provider_name?: string;
  gpu?: string;
  flavor_name?: string;
  region_name?: string;
  stock_available?: number;
  raw_unit_cost_micros?: number | string;
  provider_unit_price_micros?: number | string;
  buyer_unit_price_micros?: number | string;
  genrl_fee_bps?: number;
};

type BackendCapacityEntry = {
  signal?: {
    spoke_id?: string;
    avail_count?: number;
  };
};

type BackendLedgerEntry = {
  id?: string;
  entry_type?: string;
  debit_micros?: number;
  credit_micros?: number;
  reference_type?: string;
  reference_id?: string;
  metadata_json?: string;
  created_at?: string;
};

const PROVIDERS: Array<{
  slug: ProviderSlug;
  name: string;
  summary: string;
  icon: LucideIcon;
}> = [
  {
    slug: "runpod",
    name: "RunPod",
    summary: "Fast GPU capacity for model serving and batch inference.",
    icon: Zap,
  },
  {
    slug: "hyperstack",
    name: "Hyperstack",
    summary: "Cloud GPU capacity for larger workloads and regional routing.",
    icon: Server,
  },
  {
    slug: "lambda",
    name: "Lambda",
    summary: "GPU capacity for training, fine-tuning, and inference jobs.",
    icon: Cloud,
  },
];

const CREDIT_PACKS = [25, 50, 100, 250, 500] as const;

type CapacityManagementRow = {
  provider: BackendProProvider;
  providerMeta: ReturnType<typeof providerFromSlug>;
  offers: BackendMarketOffer[];
  liveStock: number;
  allocatedByNetwork: number;
  stockLimit: number;
  regions: string[];
  hardware: string[];
  providerUnitMicros: number;
  buyerUnitMicros: number;
  rawUnitMicros: number;
  availableMicros: number;
  escrowedMicros: number;
  marketplaceEscrowMicros: number;
  spentMicros: number;
  earnedMicros: number;
  pendingPayoutMicros: number;
  settledJobs: number;
  earningBars: Array<{ label: string; micros: number }>;
};

type CapacityProviderDraft = {
  displayName: string;
  status: string;
  visibility: ProVisibility;
  broadcastMode: ProBroadcastMode;
  pricingMode: ProPricingMode;
  adjustmentPercent: string;
  fixedPrice: string;
  minPrice: string;
  maxPrice: string;
  reserveFloor: string;
  stockLimit: string;
  allowedRegions: string;
  allowedHardware: string;
};

function readStoredAuthUserRef(): StoredAuthUserRef {
  if (typeof window === "undefined") return { email: "" };
  try {
    const raw = window.localStorage.getItem("nebula.auth.user");
    if (!raw) return { email: "" };
    const parsed = JSON.parse(raw) as { email?: unknown };
    return { email: String(parsed?.email || "").trim().toLowerCase() };
  } catch {
    return { email: "" };
  }
}

function relTime(value?: string) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const mins = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function statusLabel(value: string) {
  return String(value || "wallet").toLowerCase().replaceAll("_", " ");
}

function cleanID(value?: string) {
  return String(value || "").trim().toLowerCase();
}

function moneyMicros(value: number | string | undefined | null): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}

function signedMicros(value: number | string | undefined | null): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
}

function bpsLabel(value?: number) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n === 0) return "passthrough";
  const sign = n > 0 ? "+" : "";
  return `${sign}${(n / 100).toFixed(2)}%`;
}

function microsToCreditsInput(value: number | string | undefined | null, fractionDigits = 4) {
  const micros = moneyMicros(value);
  if (micros <= 0) return "";
  return (micros / MICRO).toFixed(fractionDigits).replace(/\.?0+$/, "");
}

function creditsToMicrosInput(value: string) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed * MICRO);
}

function parseTokenList(value: string) {
  const seen = new Set<string>();
  return value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      const key = part.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function shortMoney(value: number | string | undefined | null) {
  return formatMicros(moneyMicros(value), { suffix: "" });
}

function metadataFromLedger(row: BackendLedgerEntry): Record<string, unknown> {
  try {
    const parsed = JSON.parse(String(row.metadata_json || "{}"));
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function ledgerEntryMatchesProvider(row: BackendLedgerEntry, provider: BackendProProvider) {
  const providerID = cleanID(provider.id);
  const walletAccountID = cleanID(provider.wallet_account_id);
  const providerSlug = cleanID(provider.provider_slug);
  const reference = cleanID(`${row.reference_type || ""}:${row.reference_id || ""}`);
  const metadata = metadataFromLedger(row);
  const metadataValues = Object.values(metadata).map((value) => cleanID(String(value || "")));
  return [providerID, walletAccountID, providerSlug].some((token) => {
    if (!token) return false;
    return reference.includes(token) || metadataValues.some((value) => value.includes(token));
  });
}

function earningBarsForProvider(entries: BackendLedgerEntry[], provider: BackendProProvider) {
  const buckets = new Map<string, number>();
  for (const entry of entries) {
    const type = String(entry.entry_type || "").toLowerCase();
    const credit = signedMicros(entry.credit_micros);
    if (credit <= 0 || !type.includes("earning")) continue;
    if (!ledgerEntryMatchesProvider(entry, provider)) continue;
    const created = new Date(String(entry.created_at || ""));
    if (Number.isNaN(created.getTime())) continue;
    const key = created.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    buckets.set(key, (buckets.get(key) || 0) + credit);
  }
  return Array.from(buckets.entries())
    .slice(-7)
    .map(([label, micros]) => ({ label, micros }));
}

function userSafeProviderCreditError(value?: string) {
  const detail = String(value || "").trim();
  const normalized = detail.toLowerCase();
  if (
    normalized.includes("provider credentials") ||
    normalized.includes("api key") ||
    normalized.includes("not configured") ||
    normalized.includes("unauthorized") ||
    normalized.includes("forbidden") ||
    normalized.includes("status=401") ||
    normalized.includes("status=403")
  ) {
    return "Provider credit allocation is temporarily unavailable. GENRL is handling this behind the scenes.";
  }
  return detail;
}

function providerFromSlug(slug: string) {
  return PROVIDERS.find((provider) => provider.slug === slug) || PROVIDERS[0];
}

function normalizeProVisibility(value?: string): ProVisibility {
  return cleanID(value) === "network" ? "network" : "private";
}

function normalizeProBroadcastMode(value?: string, visibility?: string): ProBroadcastMode {
  const upper = String(value || "").trim().toUpperCase();
  if (upper === "BROADCAST_ONLY" || upper === "BOTH" || upper === "INTERNAL_ONLY") {
    if (normalizeProVisibility(visibility) === "private") return "INTERNAL_ONLY";
    if (upper === "INTERNAL_ONLY") return "BOTH";
    return upper;
  }
  return normalizeProVisibility(visibility) === "network" ? "BOTH" : "INTERNAL_ONLY";
}

function normalizeProPricingMode(value?: string): ProPricingMode {
  const upper = String(value || "").trim().toUpperCase();
  if (upper === "PREMIUM_OR_DISCOUNT" || upper === "FIXED" || upper === "FLOOR_ADJUSTED") return upper;
  return "PASSTHROUGH";
}

function draftFromCapacityProvider(provider: BackendProProvider): CapacityProviderDraft {
  const visibility = normalizeProVisibility(provider.visibility);
  return {
    displayName: String(provider.display_name || provider.provider_name || provider.provider_slug || "").trim(),
    status: String(provider.status || "active").trim().toLowerCase(),
    visibility,
    broadcastMode: normalizeProBroadcastMode(provider.broadcast_mode, visibility),
    pricingMode: normalizeProPricingMode(provider.pricing_mode),
    adjustmentPercent: ((Number(provider.provider_adjustment_bps || 0) || 0) / 100).toString(),
    fixedPrice: microsToCreditsInput(provider.fixed_unit_price_micros),
    minPrice: microsToCreditsInput(provider.min_unit_price_micros),
    maxPrice: microsToCreditsInput(provider.max_unit_price_micros),
    reserveFloor: microsToCreditsInput(provider.reserve_balance_threshold_micros, 2),
    stockLimit: Number(provider.broadcast_stock_limit || 0) > 0 ? String(provider.broadcast_stock_limit) : "",
    allowedRegions: Array.isArray(provider.allowed_regions) ? provider.allowed_regions.join(", ") : "",
    allowedHardware: Array.isArray(provider.allowed_hardware) ? provider.allowed_hardware.join(", ") : "",
  };
}

function capacityProviderPatchFromDraft(draft: CapacityProviderDraft): Record<string, unknown> {
  const visibility = normalizeProVisibility(draft.visibility);
  const broadcastMode = normalizeProBroadcastMode(draft.broadcastMode, visibility);
  const pricingMode = normalizeProPricingMode(draft.pricingMode);
  const fixedUnitPriceMicros = creditsToMicrosInput(draft.fixedPrice);
  return {
    display_name: draft.displayName.trim(),
    status: draft.status || "active",
    visibility,
    broadcast_mode: broadcastMode,
    pricing_mode: pricingMode,
    provider_adjustment_bps: Math.round((Number(draft.adjustmentPercent || 0) || 0) * 100),
    fixed_unit_price_micros: fixedUnitPriceMicros,
    min_unit_price_micros: creditsToMicrosInput(draft.minPrice),
    max_unit_price_micros: creditsToMicrosInput(draft.maxPrice),
    reserve_balance_threshold_micros: creditsToMicrosInput(draft.reserveFloor),
    broadcast_stock_limit: Math.max(0, Math.floor(Number(draft.stockLimit || 0) || 0)),
    allowed_regions: parseTokenList(draft.allowedRegions),
    allowed_hardware: parseTokenList(draft.allowedHardware),
  };
}

function idempotencyKey(provider: ProviderSlug, fundingMethod: FundingMethod) {
  const random =
    typeof window !== "undefined" && window.crypto && "randomUUID" in window.crypto
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `store-${provider}-${fundingMethod}-${random}`;
}

function StorePage() {
  const {
    stripe_status: stripeStatus,
    checkout_id: checkoutID,
    session_id: sessionID,
    provider_order_id: providerOrderID,
  } = Route.useSearch();
  const storedUser = useMemo(() => readStoredAuthUserRef(), []);
  const [selectedProvider, setSelectedProvider] = useState<ProviderSlug>("runpod");
  const [amount, setAmount] = useState(100);
  const [fundingMethod, setFundingMethod] = useState<FundingMethod>("genrl_credits");
  const [balance, setBalance] = useState<BackendCreditBalance>({});
  const [providerAssets, setProviderAssets] = useState<BackendProviderCreditAsset[]>([]);
  const [orders, setOrders] = useState<BackendProviderCreditOrder[]>([]);
  const [proProviders, setProProviders] = useState<BackendProProvider[]>([]);
  const [marketOffers, setMarketOffers] = useState<BackendMarketOffer[]>([]);
  const [capacityEntries, setCapacityEntries] = useState<BackendCapacityEntry[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<BackendLedgerEntry[]>([]);
  const [stripeConfig, setStripeConfig] = useState<BackendStripeConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingProviders, setSavingProviders] = useState<Record<string, boolean>>({});
  const [validatingProviders, setValidatingProviders] = useState<Record<string, boolean>>({});

  const amountMicros = Math.round(Math.max(1, amount) * MICRO);
  const selected = providerFromSlug(selectedProvider);
  const availableGENRL = Number(balance.available_credit_micros || 0);
  const reservedGENRL = Number(balance.reserved_credit_micros || 0);
  const providerCreditTotal = providerAssets.reduce((sum, asset) => sum + Number(asset.available_balance_micros || 0), 0);
  const totalEarnedMicros = proProviders.reduce((sum, provider) => sum + moneyMicros(provider.earned_credit_micros), 0);
  const totalPendingPayoutMicros = proProviders.reduce((sum, provider) => sum + moneyMicros(provider.owner_pending_payout_micros), 0);
  const networkEscrowMicros = providerAssets.reduce((sum, asset) => sum + moneyMicros(asset.marketplace_escrow_balance_micros), 0);
  const networkSpokes = useMemo(() => {
    const seen = new Set<string>();
    for (const entry of capacityEntries) {
      const spokeID = String(entry.signal?.spoke_id || "").trim();
      const availCount = Number(entry.signal?.avail_count || 0);
      if (spokeID && availCount > 0) seen.add(spokeID);
    }
    return seen.size;
  }, [capacityEntries]);
  const canUseGENRL = availableGENRL >= amountMicros;
  const stripeReady = Boolean(stripeConfig?.ready);
  const canSubmit =
    amountMicros >= MICRO && (fundingMethod === "stripe" ? stripeReady : canUseGENRL);

  const assetByProvider = useMemo(() => {
    const next = new Map<string, BackendProviderCreditAsset>();
    for (const asset of providerAssets) {
      next.set(String(asset.provider_id || "").toLowerCase(), asset);
    }
    return next;
  }, [providerAssets]);

  const proProviderRows = useMemo<CapacityManagementRow[]>(() => {
    return proProviders.map((provider) => {
      const providerID = cleanID(provider.id);
      const slug = cleanID(provider.provider_slug);
      const offers = marketOffers.filter((offer) => {
        const source = cleanID(offer.source);
        if (source) return source === providerID;
        return cleanID(offer.provider_slug) === slug;
      });
      const liveStock = offers.reduce((sum, offer) => sum + Math.max(0, Number(offer.stock_available || 0)), 0);
      const stockLimit = Math.max(0, Number(provider.broadcast_stock_limit || 0));
      const allocatedByNetwork = stockLimit > 0 ? Math.max(0, stockLimit - liveStock) : Math.max(0, Number(provider.settled_jobs || 0));
      const asset = assetByProvider.get(slug);
      const regions = Array.from(
        new Set([
          ...(Array.isArray(provider.allowed_regions) ? provider.allowed_regions : []),
          ...offers.map((offer) => String(offer.region_name || "").trim()).filter(Boolean),
        ]),
      ).slice(0, 4);
      const hardware = Array.from(
        new Set([
          ...(Array.isArray(provider.allowed_hardware) ? provider.allowed_hardware : []),
          ...offers.map((offer) => String(offer.gpu || offer.flavor_name || "").trim()).filter(Boolean),
        ]),
      ).slice(0, 5);
      const providerUnitMicros =
        moneyMicros(provider.fixed_unit_price_micros) ||
        offers.reduce((sum, offer) => Math.max(sum, moneyMicros(offer.provider_unit_price_micros)), 0);
      const buyerUnitMicros = offers.reduce((sum, offer) => Math.max(sum, moneyMicros(offer.buyer_unit_price_micros)), 0);
      const rawUnitMicros = offers.reduce((sum, offer) => Math.max(sum, moneyMicros(offer.raw_unit_cost_micros)), 0);
      return {
        provider,
        providerMeta: providerFromSlug(provider.provider_slug),
        offers,
        liveStock,
        allocatedByNetwork,
        stockLimit,
        regions,
        hardware,
        providerUnitMicros,
        buyerUnitMicros,
        rawUnitMicros,
        availableMicros: moneyMicros(asset?.available_balance_micros),
        escrowedMicros: moneyMicros(asset?.escrowed_balance_micros),
        marketplaceEscrowMicros: moneyMicros(asset?.marketplace_escrow_balance_micros),
        spentMicros: moneyMicros(asset?.spent_balance_micros),
        earnedMicros: moneyMicros(provider.earned_credit_micros),
        pendingPayoutMicros: moneyMicros(provider.owner_pending_payout_micros),
        settledJobs: Math.max(0, Number(provider.settled_jobs || 0)),
        earningBars: earningBarsForProvider(ledgerEntries, provider),
      };
    });
  }, [assetByProvider, ledgerEntries, marketOffers, proProviders]);

  async function refreshStore(options?: { silent?: boolean }) {
    if (!options?.silent) setLoading(true);
    try {
      const [balancePayload, providerCreditPayload, stripePayload, proPayload, offersPayload, capacityPayload, ledgerPayload] = await Promise.all([
        apiRequest<BackendCreditBalance>("/api/wallet/balance", { method: "GET", timeoutMs: 12000 }),
        apiRequest<{ orders?: BackendProviderCreditOrder[]; assets?: BackendProviderCreditAsset[] }>(
          "/api/wallet/provider-credits/orders",
          { method: "GET", timeoutMs: 12000 },
        ),
        apiRequest<BackendStripeConfig>("/api/wallet/credits/config", { method: "GET", timeoutMs: 12000 }).catch(() => null),
        apiRequest<{ providers?: BackendProProvider[] }>("/api/wallet/pro/providers", {
          method: "GET",
          timeoutMs: 15000,
        }).catch(() => ({ providers: [] })),
        apiRequest<{ offers?: BackendMarketOffer[] }>("/api/v1/market/offers?source=pro_providers", {
          method: "GET",
          timeoutMs: 20000,
        }).catch(() => ({ offers: [] })),
        apiRequest<{ entries?: BackendCapacityEntry[] }>("/api/v1/capacity", {
          method: "GET",
          timeoutMs: 12000,
        }).catch(() => ({ entries: [] })),
        apiRequest<{ entries?: BackendLedgerEntry[] }>("/api/wallet/ledger?limit=200", {
          method: "GET",
          timeoutMs: 12000,
        }).catch(() => ({ entries: [] })),
      ]);
      setBalance(balancePayload || {});
      setOrders(Array.isArray(providerCreditPayload.orders) ? providerCreditPayload.orders : []);
      setProviderAssets(Array.isArray(providerCreditPayload.assets) ? providerCreditPayload.assets : []);
      setStripeConfig(stripePayload);
      setProProviders(Array.isArray(proPayload.providers) ? proPayload.providers : []);
      setMarketOffers(Array.isArray(offersPayload.offers) ? offersPayload.offers : []);
      setCapacityEntries(Array.isArray(capacityPayload.entries) ? capacityPayload.entries : []);
      setLedgerEntries(Array.isArray(ledgerPayload.entries) ? ledgerPayload.entries : []);
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Could not load provider credit store.");
      else toast.error("Could not load provider credit store.");
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }

  useEffect(() => {
    void refreshStore();
  }, []);

  useEffect(() => {
    if (!stripeStatus) return;
    if (stripeStatus === "success") {
      toast.success(providerOrderID || checkoutID || sessionID ? "Payment received. Provider credits will appear after confirmation." : "Payment received.");
      void refreshStore({ silent: true });
    } else if (stripeStatus === "cancel") {
      toast.info("Provider credit checkout cancelled.");
    }
  }, [stripeStatus, checkoutID, sessionID, providerOrderID]);

  async function submitOrder() {
    if (!canSubmit) {
      if (fundingMethod === "stripe") toast.error("Card checkout is not available right now.");
      else toast.error("Not enough GENRL credits. Add GENRL credits in Wallet or use card checkout.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = await apiRequest<{
        checkout_url?: string;
        order?: BackendProviderCreditOrder;
      }>("/api/wallet/provider-credits/orders", {
        method: "POST",
        body: {
          provider_slug: selected.slug,
          provider_name: selected.name,
          provider_credit_amount_micros: amountMicros,
          funding_method: fundingMethod,
          customer_email: storedUser.email || undefined,
          idempotency_key: idempotencyKey(selected.slug, fundingMethod),
        },
        timeoutMs: 20000,
      });

      if (fundingMethod === "stripe") {
        const checkoutURL = String(payload.checkout_url || "").trim();
        if (!checkoutURL) throw new Error("Checkout URL missing from server response.");
        window.location.assign(checkoutURL);
        return;
      }

      toast.success(`${selected.name} credits allocated.`);
      await refreshStore({ silent: true });
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Could not buy provider credits.");
      else toast.error(err instanceof Error ? err.message : "Could not buy provider credits.");
    } finally {
      setSubmitting(false);
    }
  }

  async function saveProProvider(providerID: string, body: Record<string, unknown>) {
    const id = providerID.trim();
    if (!id) return;
    if (body.pricing_mode === "FIXED" && Number(body.fixed_unit_price_micros || 0) <= 0) {
      toast.error("Fixed pricing needs a seller price above 0.");
      return;
    }
    setSavingProviders((current) => ({ ...current, [id]: true }));
    try {
      const payload = await apiRequest<{ provider?: BackendProProvider }>(
        `/api/wallet/pro/providers/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body,
          timeoutMs: 20_000,
        },
      );
      if (payload.provider) {
        setProProviders((current) => current.map((provider) => (provider.id === payload.provider?.id ? payload.provider : provider)));
      }
      toast.success("Provider capacity settings saved.");
      await refreshStore({ silent: true });
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Could not save provider settings.");
      else toast.error("Could not save provider settings.");
      throw err;
    } finally {
      setSavingProviders((current) => ({ ...current, [id]: false }));
    }
  }

  async function validateProProvider(providerID: string) {
    const id = providerID.trim();
    if (!id) return;
    setValidatingProviders((current) => ({ ...current, [id]: true }));
    try {
      const payload = await apiRequest<{ provider?: BackendProProvider }>(
        `/api/wallet/pro/providers/${encodeURIComponent(id)}/validate`,
        {
          method: "POST",
          timeoutMs: 30_000,
        },
      );
      if (payload.provider) {
        setProProviders((current) => current.map((provider) => (provider.id === payload.provider?.id ? payload.provider : provider)));
      }
      toast.success("Provider validation refreshed.");
      await refreshStore({ silent: true });
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Could not validate provider.");
      else toast.error("Could not validate provider.");
    } finally {
      setValidatingProviders((current) => ({ ...current, [id]: false }));
    }
  }

  return (
    <main className="wallet-ops-shell flex-1">
      <div className="wallet-ops-container space-y-6">
        <header className="wallet-ops-topbar">
          <div className="wallet-ops-brand">
            <div className="wallet-ops-brand-box">
              <ShoppingCart className="h-4 w-4" />
            </div>
            <div>
              <p className="wallet-ops-label !text-[var(--wallet-ops-fg)]">Store</p>
              <p className="wallet-ops-subtle text-[11px] uppercase tracking-[0.1em]">Buy capacity</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void refreshStore({ silent: true })}
            disabled={loading}
            className="wallet-ops-icon-button"
            aria-label="Refresh store"
            title="Refresh store"
          >
            <RefreshCcw className={cn("h-4 w-4", loading && "animate-spin")} />
          </button>
        </header>

        <section className="wallet-ops-panel">
          <div className="grid gap-px bg-[var(--wallet-ops-border)] xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="bg-[var(--wallet-ops-surface)] p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-primary">
                  <Coins className="h-5 w-5" />
                </div>
                <div>
                  <p className="wallet-ops-label">Compute store</p>
                  <h1 className="mt-1 text-2xl font-semibold tracking-normal text-[var(--wallet-ops-fg)]">
                    Choose where you want extra compute credit.
                  </h1>
                </div>
              </div>
              <p className="mt-4 max-w-3xl text-sm leading-6 text-[var(--wallet-ops-muted)]">
                Pick RunPod, Hyperstack, or Lambda. Pay with GENRL credits or card; GENRL allocates the provider balance behind the scenes.
              </p>

              <div className="mt-6 grid gap-px border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-border)] md:grid-cols-4">
                <StoreStat icon={Wallet} label="GENRL available" value={<CreditAmount micros={availableGENRL} suffix="" />} />
                <StoreStat icon={CheckCircle2} label="GENRL reserved" value={<CreditAmount micros={reservedGENRL} suffix="" />} />
                <StoreStat icon={Cpu} label="Provider credits" value={<CreditAmount micros={providerCreditTotal} suffix="" />} />
                <StoreStat icon={History} label="Purchases" value={<span className="wallet-ops-mono">{orders.length}</span>} />
              </div>

              <div className="mt-6 grid gap-3 lg:grid-cols-3">
                {PROVIDERS.map((provider) => {
                  const Icon = provider.icon;
                  const asset = assetByProvider.get(provider.slug);
                  const selectedCard = selectedProvider === provider.slug;
                  return (
                    <button
                      key={provider.slug}
                      type="button"
                      onClick={() => setSelectedProvider(provider.slug)}
                      className={cn(
                        "border bg-[var(--wallet-ops-surface-2)] p-4 text-left transition",
                        selectedCard
                          ? "border-[var(--wallet-ops-fg)] text-[var(--wallet-ops-fg)]"
                          : "border-[var(--wallet-ops-border)] text-[var(--wallet-ops-muted)] hover:border-[var(--wallet-ops-border-strong)] hover:text-[var(--wallet-ops-fg)]",
                      )}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)]">
                            <Icon className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold">{provider.name}</p>
                            <p className="wallet-ops-mono mt-0.5 text-[10px] uppercase">{provider.slug}</p>
                          </div>
                        </div>
                        {selectedCard && <CheckCircle2 className="h-4 w-4" />}
                      </div>
                      <p className="mt-3 min-h-[44px] text-xs leading-5">{provider.summary}</p>
                      <div className="mt-4 border-t border-[var(--wallet-ops-border)] pt-3">
                        <p className="wallet-ops-label text-[10px]">Your {provider.name} credits</p>
                        <div className="mt-1 text-sm font-semibold text-[var(--wallet-ops-fg)]">
                          <CreditAmount micros={Number(asset?.available_balance_micros || 0)} suffix="" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-[var(--wallet-ops-surface)] p-6">
              <p className="wallet-ops-label">Purchase</p>
              <h2 className="mt-1 text-xl font-semibold tracking-normal text-[var(--wallet-ops-fg)]">{selected.name} credits</h2>

              <div className="mt-5 space-y-5">
                <div>
                  <label className="wallet-ops-label text-xs">Amount</label>
                  <div className="mt-2 flex items-center gap-3">
                    <input
                      type="number"
                      min={1}
                      value={amount}
                      onChange={(event) => setAmount(Math.max(1, Number(event.target.value || 0)))}
                      className="h-12 min-w-0 flex-1 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 font-mono text-base text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)]"
                    />
                    <span className="text-sm text-[var(--wallet-ops-muted)]">{selected.name}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {CREDIT_PACKS.map((pack) => (
                      <button
                        key={pack}
                        type="button"
                        onClick={() => setAmount(pack)}
                        className={cn(
                          "border px-3 py-1.5 text-[11px] transition",
                          amount === pack
                            ? "border-[var(--wallet-ops-fg)] bg-[var(--wallet-ops-fg)] text-[var(--wallet-ops-bg)]"
                            : "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)] hover:text-[var(--wallet-ops-fg)]",
                        )}
                      >
                        {pack}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="wallet-ops-label text-xs">Pay with</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFundingMethod("genrl_credits")}
                      className={cn("wallet-ops-button justify-center", fundingMethod === "genrl_credits" && "wallet-ops-button-active")}
                    >
                      <Coins className="h-3.5 w-3.5" />
                      GENRL credits
                    </button>
                    <button
                      type="button"
                      onClick={() => setFundingMethod("stripe")}
                      className={cn("wallet-ops-button justify-center", fundingMethod === "stripe" && "wallet-ops-button-active")}
                    >
                      <CreditCard className="h-3.5 w-3.5" />
                      Card
                    </button>
                  </div>
                </div>

                <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[var(--wallet-ops-muted)]">Provider</span>
                    <span className="font-medium text-[var(--wallet-ops-fg)]">{selected.name}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="text-[var(--wallet-ops-muted)]">You receive</span>
                    <span className="font-medium text-[var(--wallet-ops-fg)]">
                      <CreditAmount micros={amountMicros} suffix="" /> {selected.name}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="text-[var(--wallet-ops-muted)]">Cost</span>
                    <span className="wallet-ops-mono">{Math.max(1, amount || 0).toFixed(2)} credits</span>
                  </div>
                </div>

                {fundingMethod === "genrl_credits" && !canUseGENRL && (
                  <div className="border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs leading-5 text-yellow-200">
                    Your GENRL wallet needs more credits for this purchase. Add GENRL credits in Wallet or use card checkout.
                  </div>
                )}
                {fundingMethod === "stripe" && !stripeReady && (
                  <div className="border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs leading-5 text-yellow-200">
                    Card checkout is temporarily unavailable.
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => void submitOrder()}
                  disabled={submitting || !canSubmit}
                  className="wallet-ops-button wallet-ops-button-active w-full justify-center disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Creating order..." : "Continue"}
                  <ArrowRight className="h-4 w-4" />
                </button>

                <p className="text-xs leading-5 text-[var(--wallet-ops-muted)]">
                  Provider accounts stay behind the scenes. You only choose the capacity and payment method.
                </p>
              </div>
            </div>
          </div>
        </section>

        <CapacityManagementSection
          rows={proProviderRows}
          proProviderCount={proProviders.length}
          networkSpokes={networkSpokes}
          totalEarnedMicros={totalEarnedMicros}
          totalPendingPayoutMicros={totalPendingPayoutMicros}
          networkEscrowMicros={networkEscrowMicros}
          savingProviders={savingProviders}
          validatingProviders={validatingProviders}
          onSaveProvider={saveProProvider}
          onValidateProvider={validateProProvider}
        />

        <section className="wallet-ops-panel">
          <header className="wallet-ops-panel-header">
            <div>
              <p className="wallet-ops-label">Purchase history</p>
              <p className="mt-1 text-[12px] text-[var(--wallet-ops-subtle)]">
                Your capacity purchases across RunPod, Hyperstack, and Lambda.
              </p>
            </div>
            <Link to="/run-workloads" className="wallet-ops-button !min-h-[36px] px-4 text-xs">
              Ask GENRL
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </header>
          <div className="p-5">
            {orders.length === 0 ? (
              <div className="flex min-h-[180px] flex-col items-center justify-center text-center">
                <div className="flex h-12 w-12 items-center justify-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)]">
                  <History className="h-5 w-5 text-[var(--wallet-ops-muted)]" />
                </div>
                <p className="mt-3 text-sm font-medium text-[var(--wallet-ops-fg)]">No provider credit purchases yet</p>
                <p className="mt-1 max-w-lg text-xs text-[var(--wallet-ops-muted)]">
                  Buy provider credits above, then use them when GENRL routes workloads.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[840px] text-sm">
                  <thead>
                    <tr className="border-b border-[var(--wallet-ops-border)] text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--wallet-ops-muted)]">
                      <th className="px-3 py-2">Provider</th>
                      <th className="px-3 py-2">Amount</th>
                      <th className="px-3 py-2">Paid with</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const provider = providerFromSlug(order.provider_slug);
                      return (
                        <tr key={order.id} className="border-b border-[var(--wallet-ops-border)] last:border-0">
                          <td className="px-3 py-3">
                            <p className="font-medium text-[var(--wallet-ops-fg)]">{order.provider_name || provider.name}</p>
                            <p className="font-mono text-[10px] uppercase text-[var(--wallet-ops-muted)]">{order.id}</p>
                          </td>
                          <td className="px-3 py-3">
                            <CreditAmount micros={order.provider_credit_amount_micros} suffix="" />
                          </td>
                          <td className="px-3 py-3 capitalize text-[var(--wallet-ops-muted)]">
                            {statusLabel(order.funding_method)}
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium capitalize text-[var(--wallet-ops-fg)]">{statusLabel(order.status)}</p>
                            <p className="text-xs capitalize text-[var(--wallet-ops-muted)]">{statusLabel(order.fulfillment_status)}</p>
                            {order.last_error && <p className="mt-1 max-w-xs truncate text-xs text-red-300">{userSafeProviderCreditError(order.last_error)}</p>}
                          </td>
                          <td className="px-3 py-3 text-right font-mono text-xs text-[var(--wallet-ops-muted)]">{relTime(order.updated_at || order.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function CapacityManagementSection({
  rows,
  proProviderCount,
  networkSpokes,
  totalEarnedMicros,
  totalPendingPayoutMicros,
  networkEscrowMicros,
  savingProviders,
  validatingProviders,
  onSaveProvider,
  onValidateProvider,
}: {
  rows: CapacityManagementRow[];
  proProviderCount: number;
  networkSpokes: number;
  totalEarnedMicros: number;
  totalPendingPayoutMicros: number;
  networkEscrowMicros: number;
  savingProviders: Record<string, boolean>;
  validatingProviders: Record<string, boolean>;
  onSaveProvider: (providerID: string, body: Record<string, unknown>) => Promise<void>;
  onValidateProvider: (providerID: string) => Promise<void>;
}) {
  const pendingAndEscrowMicros = totalPendingPayoutMicros + networkEscrowMicros;

  return (
    <section id="capacity-management" className="wallet-ops-panel">
      <header className="wallet-ops-panel-header">
        <div>
          <p className="wallet-ops-label">Capacity management</p>
          <p className="mt-1 text-[12px] text-[var(--wallet-ops-subtle)]">
            Connected-provider usage, network draw, limits, costs, remaining balances, and earnings.
          </p>
        </div>
        <Link to="/providers" className="wallet-ops-button !min-h-[36px] px-4 text-xs">
          Manage providers
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </header>

      <div className="grid gap-px bg-[var(--wallet-ops-border)] md:grid-cols-4">
        <StoreStat icon={Server} label="Connected providers" value={<span className="wallet-ops-mono">{proProviderCount}</span>} />
        <StoreStat icon={Activity} label="Live network feeds" value={<span className="wallet-ops-mono">{networkSpokes}</span>} />
        <StoreStat icon={BadgeDollarSign} label="Earned" value={<CreditAmount micros={totalEarnedMicros} suffix="" />} />
        <StoreStat icon={Lock} label="Pending / escrow" value={<CreditAmount micros={pendingAndEscrowMicros} suffix="" />} />
      </div>

      {rows.length === 0 ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center p-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)]">
            <Gauge className="h-5 w-5" />
          </div>
          <p className="mt-3 text-sm font-medium text-[var(--wallet-ops-fg)]">No seller capacity connected yet</p>
          <p className="mt-1 max-w-xl text-xs leading-5 text-[var(--wallet-ops-muted)]">
            Connect a provider in Settings, then publish eligible private or network capacity from here.
          </p>
          <Link to="/providers" className="wallet-ops-button mt-4 !min-h-[36px] px-4 text-xs">
            Add provider
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 p-5 xl:grid-cols-2">
          {rows.map((row) => (
            <CapacityProviderCard
              key={row.provider.id}
              row={row}
              saving={Boolean(savingProviders[row.provider.id])}
              validating={Boolean(validatingProviders[row.provider.id])}
              onSave={onSaveProvider}
              onValidate={onValidateProvider}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CapacityProviderCard({
  row,
  saving,
  validating,
  onSave,
  onValidate,
}: {
  row: CapacityManagementRow;
  saving: boolean;
  validating: boolean;
  onSave: (providerID: string, body: Record<string, unknown>) => Promise<void>;
  onValidate: (providerID: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CapacityProviderDraft>(() => draftFromCapacityProvider(row.provider));
  const Icon = row.providerMeta.icon;
  const providerName = row.provider.display_name || row.provider.provider_name || row.providerMeta.name;
  const status = statusLabel(row.provider.status || "unknown");
  const visibility = statusLabel(row.provider.visibility || "private");
  const broadcastMode = statusLabel(row.provider.broadcast_mode || "internal_only");
  const pricingMode = statusLabel(row.provider.pricing_mode || "passthrough");
  const sellerPrice =
    row.providerUnitMicros > 0
      ? `${shortMoney(row.providerUnitMicros)} / h`
      : row.provider.pricing_mode === "PREMIUM_OR_DISCOUNT" || row.provider.pricing_mode === "premium_or_discount"
        ? bpsLabel(row.provider.provider_adjustment_bps)
        : pricingMode;
  const buyerPrice = row.buyerUnitMicros > 0 ? `${shortMoney(row.buyerUnitMicros)} / h` : "-";
  const rawCost = row.rawUnitMicros > 0 ? `${shortMoney(row.rawUnitMicros)} / h` : "-";
  const limitLabel = row.stockLimit > 0 ? String(row.stockLimit) : "No cap";
  const remainingStock = row.stockLimit > 0 ? Math.max(0, row.stockLimit - row.allocatedByNetwork) : row.liveStock;
  const busy = saving || validating;

  useEffect(() => {
    if (!editing) setDraft(draftFromCapacityProvider(row.provider));
  }, [editing, row.provider.id, row.provider.updated_at]);

  function updateDraft(patch: Partial<CapacityProviderDraft>) {
    setDraft((current) => {
      const next = { ...current, ...patch };
      if (patch.visibility === "private") {
        next.broadcastMode = "INTERNAL_ONLY";
      }
      if (patch.visibility === "network" && next.broadcastMode === "INTERNAL_ONLY") {
        next.broadcastMode = "BOTH";
      }
      return next;
    });
  }

  async function saveDraft() {
    const patch = capacityProviderPatchFromDraft(draft);
    if (patch.pricing_mode === "FIXED" && Number(patch.fixed_unit_price_micros || 0) <= 0) {
      toast.error("Fixed pricing needs a seller price above 0.");
      return;
    }
    await onSave(row.provider.id, patch);
    setEditing(false);
  }

  return (
    <article className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-semibold tracking-normal text-[var(--wallet-ops-fg)]">{providerName}</h3>
            <p className="mt-1 truncate text-[11px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
              {row.provider.provider_slug} · {visibility} · {broadcastMode}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em]", providerStatusClass(row.provider.status))}>
            {status}
          </span>
          <button
            type="button"
            onClick={() => void onValidate(row.provider.id)}
            disabled={busy}
            className="wallet-ops-button !min-h-[32px] px-3 text-[10px]"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {validating ? "Checking" : "Validate"}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(draftFromCapacityProvider(row.provider));
              setEditing((value) => !value);
            }}
            disabled={busy}
            className={cn("wallet-ops-button !min-h-[32px] px-3 text-[10px]", editing && "wallet-ops-button-active")}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {editing ? "Close" : "Manage"}
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-px border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-border)] sm:grid-cols-2">
        <CapacityMetric label="Network live" value={String(row.liveStock)} hint={`${row.offers.length} offers`} />
        <CapacityMetric label="Consumed" value={String(row.allocatedByNetwork)} hint={`${remainingStock} remaining`} />
        <CapacityMetric label="Limit" value={limitLabel} hint={`${row.settledJobs} settled jobs`} />
        <CapacityMetric label="Provider left" value={<CreditAmount micros={row.availableMicros} suffix="" />} hint={`${shortMoney(row.spentMicros)} spent`} />
      </div>

      <div className="mt-3 grid gap-px border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-border)] sm:grid-cols-2 lg:grid-cols-4">
        <CapacityMetric label="Earned" value={<CreditAmount micros={row.earnedMicros} suffix="" />} />
        <CapacityMetric label="Pending" value={<CreditAmount micros={row.pendingPayoutMicros} suffix="" />} />
        <CapacityMetric label="Escrowed" value={<CreditAmount micros={row.escrowedMicros + row.marketplaceEscrowMicros} suffix="" />} />
        <CapacityMetric label="Reserve floor" value={<CreditAmount micros={moneyMicros(row.provider.reserve_balance_threshold_micros)} suffix="" />} />
      </div>

      {editing && (
        <div className="mt-3 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="wallet-ops-label text-[9px]">Direct provider controls</p>
              <p className="mt-1 text-[11px] text-[var(--wallet-ops-muted)]">
                Changes are applied to routing only after save.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setDraft(draftFromCapacityProvider(row.provider));
                  setEditing(false);
                }}
                disabled={busy}
                className="wallet-ops-button !min-h-[32px] px-3 text-[10px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveDraft()}
                disabled={busy}
                className="wallet-ops-button wallet-ops-button-active !min-h-[32px] px-3 text-[10px]"
              >
                <Save className="h-3.5 w-3.5" />
                {saving ? "Saving" : "Save"}
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            <CapacityControl label="Name">
              <input
                value={draft.displayName}
                onChange={(event) => updateDraft({ displayName: event.target.value })}
                className="wallet-ops-input"
              />
            </CapacityControl>
            <CapacityControl label="Status">
              <select
                value={draft.status}
                onChange={(event) => updateDraft({ status: event.target.value })}
                className="wallet-ops-input"
              >
                <option value="active">Active</option>
                <option value="degraded">Degraded</option>
                <option value="suspended">Suspended</option>
                <option value="revoked">Revoked</option>
              </select>
            </CapacityControl>
            <CapacityControl label="Visibility">
              <select
                value={draft.visibility}
                onChange={(event) => updateDraft({ visibility: event.target.value as ProVisibility })}
                className="wallet-ops-input"
              >
                <option value="private">Private</option>
                <option value="network">Network</option>
              </select>
            </CapacityControl>
            <CapacityControl label="Use mode">
              <select
                value={draft.broadcastMode}
                onChange={(event) => updateDraft({ broadcastMode: event.target.value as ProBroadcastMode })}
                disabled={draft.visibility === "private"}
                className="wallet-ops-input disabled:opacity-60"
              >
                <option value="INTERNAL_ONLY">Internal only</option>
                <option value="BOTH">Internal + network</option>
                <option value="BROADCAST_ONLY">Network only</option>
              </select>
            </CapacityControl>
            <CapacityControl label="Pricing">
              <select
                value={draft.pricingMode}
                onChange={(event) => updateDraft({ pricingMode: event.target.value as ProPricingMode })}
                className="wallet-ops-input"
              >
                <option value="PASSTHROUGH">Provider cost</option>
                <option value="PREMIUM_OR_DISCOUNT">Markup / discount</option>
                <option value="FIXED">Fixed seller price</option>
                <option value="FLOOR_ADJUSTED">Floor adjusted</option>
              </select>
            </CapacityControl>
            <CapacityControl label="Adjustment %">
              <input
                type="number"
                step="0.1"
                value={draft.adjustmentPercent}
                onChange={(event) => updateDraft({ adjustmentPercent: event.target.value })}
                className="wallet-ops-input wallet-ops-mono"
              />
            </CapacityControl>
            <CapacityControl label="Fixed price / h">
              <input
                type="number"
                min="0"
                step="0.0001"
                value={draft.fixedPrice}
                onChange={(event) => updateDraft({ fixedPrice: event.target.value })}
                placeholder="credits"
                className="wallet-ops-input wallet-ops-mono"
              />
            </CapacityControl>
            <CapacityControl label="Min price / h">
              <input
                type="number"
                min="0"
                step="0.0001"
                value={draft.minPrice}
                onChange={(event) => updateDraft({ minPrice: event.target.value })}
                placeholder="optional"
                className="wallet-ops-input wallet-ops-mono"
              />
            </CapacityControl>
            <CapacityControl label="Max price / h">
              <input
                type="number"
                min="0"
                step="0.0001"
                value={draft.maxPrice}
                onChange={(event) => updateDraft({ maxPrice: event.target.value })}
                placeholder="optional"
                className="wallet-ops-input wallet-ops-mono"
              />
            </CapacityControl>
            <CapacityControl label="Stock cap">
              <input
                type="number"
                min="0"
                step="1"
                value={draft.stockLimit}
                onChange={(event) => updateDraft({ stockLimit: event.target.value })}
                placeholder="all"
                className="wallet-ops-input wallet-ops-mono"
              />
            </CapacityControl>
            <CapacityControl label="Reserve floor">
              <input
                type="number"
                min="0"
                step="0.01"
                value={draft.reserveFloor}
                onChange={(event) => updateDraft({ reserveFloor: event.target.value })}
                placeholder="credits"
                className="wallet-ops-input wallet-ops-mono"
              />
            </CapacityControl>
            <CapacityControl label="Allowed regions">
              <input
                value={draft.allowedRegions}
                onChange={(event) => updateDraft({ allowedRegions: event.target.value })}
                placeholder={row.regions.slice(0, 3).join(", ") || "all"}
                className="wallet-ops-input wallet-ops-mono"
              />
            </CapacityControl>
            <CapacityControl label="Allowed hardware">
              <input
                value={draft.allowedHardware}
                onChange={(event) => updateDraft({ allowedHardware: event.target.value })}
                placeholder={row.hardware.slice(0, 3).join(", ") || "all"}
                className="wallet-ops-input wallet-ops-mono lg:col-span-2"
              />
            </CapacityControl>
          </div>
        </div>
      )}

      <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
        <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <MiniFact label="Raw cost" value={rawCost} />
            <MiniFact label="Seller price" value={sellerPrice} />
            <MiniFact label="Buyer top price" value={buyerPrice} />
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <CapacityChips label="Regions" values={row.regions} fallback="All regions" />
            <CapacityChips label="Hardware" values={row.hardware} fallback="All hardware" />
          </div>
        </div>
        <EarningBars bars={row.earningBars} fallbackMicros={row.earnedMicros} />
      </div>

      {(row.provider.last_validated_at || row.provider.last_settlement_at || row.provider.last_error) && (
        <div className="mt-3 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-3 py-2 text-[11px] text-[var(--wallet-ops-muted)]">
          <span>Validated {relTime(row.provider.last_validated_at)}</span>
          <span className="mx-2 text-[var(--wallet-ops-border-strong)]">/</span>
          <span>Settlement {relTime(row.provider.last_settlement_at)}</span>
          {row.provider.last_error && <p className="mt-1 truncate text-red-300">{row.provider.last_error}</p>}
        </div>
      )}
    </article>
  );
}

function CapacityControl({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[9px] font-semibold uppercase tracking-[0.14em] text-[var(--wallet-ops-muted)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function CapacityMetric({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="min-h-[78px] bg-[var(--wallet-ops-surface)] p-3">
      <p className="wallet-ops-label text-[9px]">{label}</p>
      <div className="mt-1 text-[15px] font-semibold text-[var(--wallet-ops-fg)]">{value}</div>
      {hint && <p className="mt-1 truncate text-[11px] text-[var(--wallet-ops-muted)]">{hint}</p>}
    </div>
  );
}

function MiniFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="wallet-ops-label text-[9px]">{label}</p>
      <div className="mt-1 truncate text-[12px] font-semibold text-[var(--wallet-ops-fg)]">{value}</div>
    </div>
  );
}

function CapacityChips({
  label,
  values,
  fallback,
}: {
  label: string;
  values: string[];
  fallback: string;
}) {
  const display = values.length > 0 ? values : [fallback];
  return (
    <div>
      <p className="wallet-ops-label text-[9px]">{label}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {display.map((value) => (
          <span
            key={value}
            className="max-w-full truncate border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-2 py-1 text-[10px] text-[var(--wallet-ops-muted)]"
          >
            {value}
          </span>
        ))}
      </div>
    </div>
  );
}

function EarningBars({
  bars,
  fallbackMicros,
}: {
  bars: Array<{ label: string; micros: number }>;
  fallbackMicros: number;
}) {
  const displayBars = bars.length > 0 ? bars : fallbackMicros > 0 ? [{ label: "Total", micros: fallbackMicros }] : [];
  const max = Math.max(...displayBars.map((bar) => bar.micros), 1);
  return (
    <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="wallet-ops-label text-[9px]">Earnings over time</p>
        <BarChart3 className="h-3.5 w-3.5 text-[var(--wallet-ops-muted)]" />
      </div>
      {displayBars.length === 0 ? (
        <p className="mt-5 text-[11px] text-[var(--wallet-ops-muted)]">No settled earnings yet</p>
      ) : (
        <div className="mt-3 flex h-[88px] items-end gap-1.5">
          {displayBars.map((bar) => (
            <div key={bar.label} className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <span
                className="w-full min-w-[10px] bg-[var(--wallet-ops-fg)]"
                style={{ height: `${Math.max(8, (bar.micros / max) * 64)}px` }}
                title={`${bar.label}: ${shortMoney(bar.micros)}`}
              />
              <span className="max-w-full truncate text-[9px] text-[var(--wallet-ops-muted)]">{bar.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function providerStatusClass(status?: string) {
  const normalized = cleanID(status);
  if (normalized === "active" || normalized === "ready" || normalized === "connected") {
    return "border-emerald-600/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  }
  if (normalized === "paused" || normalized === "private" || normalized === "pending") {
    return "border-amber-600/30 bg-amber-500/10 text-amber-800 dark:text-amber-300";
  }
  if (normalized === "error" || normalized === "failed" || normalized === "degraded") {
    return "border-red-600/30 bg-red-500/10 text-red-700 dark:text-red-300";
  }
  return "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)]";
}

function StoreStat({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="bg-[var(--wallet-ops-surface)] p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="wallet-ops-label text-[10px]">{label}</p>
          <div className="mt-1 text-sm font-semibold text-[var(--wallet-ops-fg)]">{value}</div>
        </div>
      </div>
    </div>
  );
}
